// /frontend/src/pages/AEPWizard.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Zap, CalendarDays, Send, Eye, MessageSquare, AlertCircle, XCircle,
  CheckCircle2, Plus, Edit2, Download, RefreshCw, Star, Eye as EyeIcon,
  ListChecks, PhoneCall, Mail, Calendar, UserPlus, Filter, ArrowUpRight,
  HelpCircle, Trash2, Save, Shield, Award, AlertTriangle, LayoutDashboard,
  FileText, Users, Copy, Check, ExternalLink, TrendingUp, Target, Clock,
  BookOpen, ChevronDown, ChevronUp, Info, DollarSign, Activity, Clipboard,
} from "lucide-react";
import {
  getAepTemplates, createAepTemplate, updateAepTemplate, deleteAepTemplate,
  getAepAutomations, updateAepAutomations,
  getAepCountdownContacts, addAepCountdownContact, updateAepCountdownContact,
  deleteAepCountdownContact, sendAepDrip,
  getAepActivity, resendAepActivity,
  getAepAnalytics, getAepMergeTags,
} from "../api";

/* -------------------- constants -------------------- */

const TABS = [
  { id: "dashboard",       label: "Dashboard",       icon: LayoutDashboard },
  { id: "compliance",      label: "Compliance Hub",  icon: Shield },
  { id: "certifications",  label: "Certifications",  icon: Award },
  { id: "anoc",            label: "ANOC Center",     icon: AlertTriangle },
  { id: "automation",      label: "Automation",      icon: Zap },
  { id: "templates",       label: "Templates",       icon: Star },
  { id: "countdown",       label: "Countdown List",  icon: Users },
  { id: "activity",        label: "Activity Log",    icon: ListChecks },
];

const AEP_KEY_DATES = [
  { label: "AHIP Opens", date: "Jun 2026", note: "~$175 exam. 50 questions, 90% to pass.", color: "blue", done: false },
  { label: "Carrier Certs Open", date: "Jul 2026", note: "UHC, Humana (Jul 8), Cigna, Aetna, Anthem", color: "blue", done: false },
  { label: "ANOC Deadline", date: "Sep 30", note: "All MA/PDP plans must mail Annual Notice of Change", color: "yellow", done: false },
  { label: "Marketing Window Opens", date: "Oct 1", note: "CY2027 rule changes take effect (48-hr SOA removed)", color: "green", done: false },
  { label: "AEP Opens", date: "Oct 15", note: "54-day window. Applications accepted.", color: "green", done: false },
  { label: "AEP Closes", date: "Dec 7", note: "Last day to submit applications for Jan 1 effective date", color: "red", done: false },
  { label: "Plans Effective", date: "Jan 1, 2027", note: "All AEP enrollments take effect", color: "purple", done: false },
];

const CARRIERS = [
  { key: "uhc",    name: "UnitedHealthcare",  opens: "July 2026",      notes: "Accepts AHIP or NABIP. Use Jarvis portal." },
  { key: "humana", name: "Humana",             opens: "~Jul 8, 2026",   notes: "$50 AHIP discount via Humana portal." },
  { key: "aetna",  name: "Aetna/CVS",          opens: "Late Jun 2026",  notes: "Requires AHIP only (not NABIP)." },
  { key: "cigna",  name: "Cigna",              opens: "~Jul 21, 2026",  notes: "Cigna Producers' University." },
  { key: "anthem", name: "Anthem/BCBS",        opens: "Jul 2026",       notes: "Accepts AHIP or NABIP." },
  { key: "wellcare",name: "WellCare",          opens: "Late Jul 2026",  notes: "Annual Certification Training (ACT)." },
  { key: "molina", name: "Molina",             opens: "Summer 2026",    notes: "Offers AHIP discount via portal." },
];

const TPMO_DISCLAIMER = `"We do not offer every plan available in your area. Currently we represent [X] organization(s) which offer(s) [X] product(s) in your area. Please contact Medicare.gov, 1-800-MEDICARE, or your local State Health Insurance Program (SHIP) to get information on all of your options."`;

const COMPLIANCE_RULES = [
  {
    title: "Scope of Appointment (SOA)",
    icon: Clipboard,
    color: "blue",
    rules: [
      "SOA must be obtained before discussing MA or PDP benefits in a one-on-one meeting.",
      "48-hour advance rule ELIMINATED effective Oct 1, 2026 (CY2027 Final Rule) — agents can meet same day SOA is signed.",
      "SOA valid for 12 months. Retain for 10 years.",
      "Acceptable: paper, telephonic (recorded), electronic (DocuSign).",
      "Walk-ins and inbound calls initiated by beneficiary are exceptions.",
    ],
    badge: "2027 Rule Change: 48hr Wait Removed Oct 1, 2026",
    badgeColor: "green",
  },
  {
    title: "Call Recording",
    icon: PhoneCall,
    color: "red",
    rules: [
      "ALL marketing, sales, and enrollment calls must be recorded in their entirety.",
      "Virtual/video appointments: audio must be recorded (video not required).",
      "In-person meetings: recording not required.",
      "Retain recordings for 10 years in a HIPAA-compliant manner.",
      "If beneficiary declines recording: end call. Cannot conduct phone enrollment without consent.",
    ],
  },
  {
    title: "Permission to Contact (PTC)",
    icon: Mail,
    color: "purple",
    rules: [
      "PTC required before any marketing outreach (call, text, email).",
      "Valid for 12 months from signature date.",
      "Inbound calls from beneficiary = PTC for that call.",
      "Contact hours: 8:00 AM – 9:00 PM in beneficiary's local time zone.",
      "Honor DNC requests within 10 business days. Maintain internal DNC list.",
    ],
  },
  {
    title: "Marketing Restrictions",
    icon: AlertCircle,
    color: "orange",
    rules: [
      "No cold calling, door-to-door solicitation, or unsolicited texts/emails.",
      "No robocalls without prior express written consent.",
      "No marketing in healthcare settings (waiting rooms, pharmacies, exam rooms).",
      "No comparing competitor plans by name negatively.",
      "No superlatives ('best plan', 'highest rated') without CMS-approved data.",
      "Gift limits: max $15/item, $75/person/year. No cash or gift cards to major retailers.",
    ],
  },
  {
    title: "Event Rules",
    icon: Calendar,
    color: "teal",
    rules: [
      "Educational events: NO plan-specific presentations, enrollment forms, or SOA collection.",
      "Sales events: Must notify attendees it's a sales event. SOA required before plan discussion.",
      "12-hour gap between educational and sales events at same location (eliminated Oct 1, 2026).",
      "Submit events to carriers at least 2 weeks in advance.",
      "Must include accessibility disclaimer in invitations.",
    ],
    badge: "2027 Rule Change: 12hr Gap Removed Oct 1, 2026",
    badgeColor: "green",
  },
  {
    title: "TPMO Disclaimer",
    icon: FileText,
    color: "gray",
    rules: [
      "Required: first 60 seconds of ANY sales call (verbatim).",
      "Required on all print/digital marketing materials and website.",
      "Required in emails, online chat, and electronic communications.",
      "Language assistance notice required on all materials (as of Sep 30, 2025).",
      "Data sharing between TPMOs requires separate prior express written consent.",
    ],
  },
];

const ANOC_SEQUENCE = [
  {
    phase: "Day 1–2",
    channel: "SMS + Email",
    icon: MessageSquare,
    color: "blue",
    title: "ANOC Arrived Notification",
    template: `Hi {ClientName}, your Medicare plan just mailed your 2026 Annual Notice of Change. I've reviewed the changes and want to make sure everything still works for you. Reply or call {AgentPhone} to schedule your free review. Reply STOP to opt out.`,
  },
  {
    phase: "Day 3–7",
    channel: "Phone + Voicemail",
    icon: PhoneCall,
    color: "yellow",
    title: "Annual Review Outreach",
    template: `Hi {ClientName}, this is {AgentName}. I'm calling because your Medicare plan has some changes for 2026 that I want to walk you through — specifically your [premium/drug/provider changes]. This is your free annual review. Give me a call at {AgentPhone}. I have time this week.`,
  },
  {
    phase: "Day 8–14",
    channel: "Follow-Up Call",
    icon: RefreshCw,
    color: "orange",
    title: "Non-Responder Follow-Up",
    template: `Hi {ClientName}, I've tried reaching you a couple times about your 2026 Medicare changes. AEP closes December 7th — that's your only window this year to make changes. I don't want you to miss it. Please call me at {AgentPhone} or reply to this message.`,
  },
  {
    phase: "Dec 1–7",
    channel: "Urgency Push",
    icon: AlertTriangle,
    color: "red",
    title: "AEP Deadline Push",
    template: `Hi {ClientName}, AEP closes December 7th — just days away. This is your last chance to review your 2026 Medicare coverage. Call {AgentPhone} for a quick 15-minute review. Don't let the deadline pass. Reply STOP to opt out.`,
  },
];

/* -------------------- helpers -------------------- */

function getNextOct15() {
  const now = new Date();
  const year =
    now.getMonth() > 9 || (now.getMonth() === 9 && now.getDate() > 15)
      ? now.getFullYear() + 1
      : now.getFullYear();
  return new Date(year, 9, 15, 0, 0, 0, 0);
}

function breakdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  return {
    d: Math.floor(total / 86400),
    h: Math.floor((total % 86400) / 3600),
    m: Math.floor((total % 3600) / 60),
    s: total % 60,
  };
}

function formatTime(timestamp) {
  if (!timestamp) return "";
  const d = new Date(timestamp);
  const now = new Date();
  const diffMins = Math.floor((now - d) / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback((text) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);
  return [copied, copy];
}

/* ==================== MAIN COMPONENT ==================== */

export default function AEPWizard() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [now, setNow] = useState(new Date());
  const target = useMemo(() => getNextOct15(), []);

  // Certification tracking (localStorage)
  const [certs, setCerts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("aep-certs-v2") || "{}"); }
    catch { return {}; }
  });

  // AEP Goals (localStorage)
  const [goals, setGoals] = useState(() => {
    try { return JSON.parse(localStorage.getItem("aep-goals") || '{"appointments":100,"newEnrollments":40,"bookSize":200}'); }
    catch { return { appointments: 100, newEnrollments: 40, bookSize: 200 }; }
  });

  // Pre-call compliance checklist (per-session)
  const [preCallChecks, setPreCallChecks] = useState({
    ptc: false, soa: false, recording: false, tpmo: false,
  });

  // Existing API state
  const [templates, setTemplates] = useState([]);
  const [activity, setActivity] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [mergeTags, setMergeTags] = useState([]);
  const [automations, setAutomations] = useState({
    preAEP60: true, preAEP30: true, preAEP14: true, preAEP7: true,
    preAEP3: true, preAEP1: true, anocExplainer: true, bookingNudges: true,
    voicemailDropUI: false, requireApproval: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTemplate, setSearchTemplate] = useState("");
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const remaining = breakdown(target.getTime() - now.getTime());

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [tRes, aRes, cRes, actRes, anRes, mRes] = await Promise.all([
        getAepTemplates().catch(() => ({ data: [] })),
        getAepAutomations().catch(() => ({ data: {} })),
        getAepCountdownContacts().catch(() => ({ data: [] })),
        getAepActivity({ limit: 50 }).catch(() => ({ data: [] })),
        getAepAnalytics({ days: 30 }).catch(() => ({ data: [] })),
        getAepMergeTags().catch(() => ({ data: [] })),
      ]);
      if (tRes.data) setTemplates(tRes.data);
      if (aRes.data) setAutomations(p => ({ ...p, ...aRes.data }));
      if (cRes.data) setContacts(cRes.data);
      if (actRes.data) setActivity(actRes.data);
      if (anRes.data) setAnalytics(anRes.data);
      if (mRes.data) setMergeTags(mRes.data);
    } catch (err) {
      setError("Failed to load data. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Cert helpers
  const updateCert = (key, field, value) => {
    const updated = { ...certs, [key]: { ...(certs[key] || {}), [field]: value } };
    setCerts(updated);
    localStorage.setItem("aep-certs-v2", JSON.stringify(updated));
  };

  // Goal helpers
  const updateGoal = (key, value) => {
    const updated = { ...goals, [key]: Number(value) || 0 };
    setGoals(updated);
    localStorage.setItem("aep-goals", JSON.stringify(updated));
  };

  // Toggle automation
  async function toggleAuto(key) {
    const newVal = !automations[key];
    setAutomations(a => ({ ...a, [key]: newVal }));
    try { await updateAepAutomations({ ...automations, [key]: newVal }); }
    catch { setAutomations(a => ({ ...a, [key]: !newVal })); }
  }

  // Activity
  async function handleResend(idx) {
    const item = activity[idx];
    if (!item?.id) return;
    try {
      await resendAepActivity(item.id);
      setActivity(p => p.map((a, i) => i === idx ? { ...a, status: "resent" } : a));
    } catch { /* noop */ }
  }

  function handleExportCSV() {
    const csv = activity.map(a => `${a.time},${a.type},${a.to},${a.subject},${a.status}`).join("\n");
    const blob = new Blob(["Time,Type,To,Subject,Status\n" + csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = "aep-activity.csv"; link.click();
  }

  // Templates
  async function handleSaveTemplate(e) {
    e.preventDefault();
    if (!editingTemplate) return;
    setSaving(true);
    try {
      if (editingTemplate.id) {
        const res = await updateAepTemplate(editingTemplate.id, editingTemplate);
        if (res.data) setTemplates(p => p.map(t => t.id === editingTemplate.id ? res.data : t));
      } else {
        const res = await createAepTemplate(editingTemplate);
        if (res.data) setTemplates(p => [...p, res.data]);
      }
      setEditingTemplate(null);
    } catch { alert("Failed to save template"); }
    finally { setSaving(false); }
  }

  async function handleDeleteTemplate(tpl) {
    if (tpl.is_system) { alert("Cannot delete system templates"); return; }
    if (!window.confirm(`Delete "${tpl.title}"?`)) return;
    try {
      await deleteAepTemplate(tpl.id);
      setTemplates(p => p.filter(t => t.id !== tpl.id));
    } catch { alert("Failed to delete"); }
  }

  // Contacts
  async function saveContact(e) {
    e.preventDefault();
    setSaving(true);
    const form = new FormData(e.currentTarget);
    const payload = {
      firstName: form.get("firstName")?.trim() || "",
      lastName: form.get("lastName")?.trim() || "",
      phone: form.get("phone")?.trim() || "",
      email: form.get("email")?.trim() || "",
      zip: form.get("zip")?.trim() || "",
      county: form.get("county")?.trim() || "",
      dob: form.get("dob") || "",
      language: form.get("language") || "English",
      source: form.get("source") || "Other",
      notes: form.get("notes") || "",
      permissionToContact: form.get("ptc") === "on",
      status: form.get("status") || "New",
      newsletter: form.get("newsletter") === "on",
      outreachPlan: {
        twoMonths: form.get("twoMonths") === "on",
        oneMonth: form.get("oneMonth") === "on",
        twoWeeks: form.get("twoWeeks") === "on",
        oneWeek: form.get("oneWeek") === "on",
        aepLive: form.get("aepLive") === "on",
      },
    };
    if (!payload.phone && !payload.email) {
      alert("Please provide at least a phone or email."); setSaving(false); return;
    }
    try {
      if (editing) {
        await updateAepCountdownContact(editing.id, payload);
        setContacts(p => p.map(c => c.id === editing.id ? { ...c, ...payload } : c));
      } else {
        const res = await addAepCountdownContact(payload);
        if (res.data) setContacts(p => [{ ...payload, id: res.data.id }, ...p]);
      }
      setAddOpen(false);
    } catch { alert("Failed to save contact"); }
    finally { setSaving(false); }
  }

  async function sendNextDrip(c) {
    try {
      await sendAepDrip(c.id, { channel: "email" });
      setContacts(p => p.map(x => x.id === c.id ? { ...x, status: x.status === "New" ? "Warm" : x.status } : x));
      alert(`Queued drip to ${c.firstName} ${c.lastName}`);
      loadData();
    } catch { alert("Failed to send drip"); }
  }

  async function handleDeleteContact(c) {
    if (!window.confirm(`Delete ${c.firstName} ${c.lastName}?`)) return;
    try {
      await deleteAepCountdownContact(c.id);
      setContacts(p => p.filter(x => x.id !== c.id));
    } catch { /* noop */ }
  }

  /* ==================== RENDER ==================== */

  if (loading && templates.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-blue-50 to-blue-100">
        <div className="text-center">
          <Zap className="w-12 h-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-blue-900 font-semibold">Loading AEP Wizard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-blue-100">
      {/* HEADER */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-blue-100 shadow-sm">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-blue-800" />
            <h1 className="text-xl font-black text-blue-900 tracking-tight">AEP Wizard</h1>
            <span className="hidden sm:block text-xs font-semibold text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
              One-Stop Shop
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <CalendarDays className="w-4 h-4 text-blue-700" />
            <span className="font-bold text-blue-900">
              <span className="hidden sm:inline text-xs text-blue-600 font-semibold">AEP in </span>
              {remaining.d}d {remaining.h}h {remaining.m}m {remaining.s}s
            </span>
          </div>
        </div>
        {/* Tab bar */}
        <div className="flex gap-0.5 overflow-x-auto px-4 sm:px-6 pb-0 scrollbar-none">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${
                  isActive
                    ? "border-blue-600 text-blue-700 bg-blue-50/50"
                    : "border-transparent text-gray-500 hover:text-blue-700 hover:bg-blue-50/30"
                }`}
              >
                <Icon size={15} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden text-xs">{tab.label.split(" ")[0]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="mx-4 sm:mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* TAB CONTENT */}
      <div className="px-3 sm:px-6 py-4 sm:py-6">
        {activeTab === "dashboard" && (
          <DashboardTab remaining={remaining} goals={goals} updateGoal={updateGoal} contacts={contacts} activity={activity} automations={automations} />
        )}
        {activeTab === "compliance" && (
          <ComplianceTab preCallChecks={preCallChecks} setPreCallChecks={setPreCallChecks} />
        )}
        {activeTab === "certifications" && (
          <CertificationsTab certs={certs} updateCert={updateCert} />
        )}
        {activeTab === "anoc" && (
          <AnocTab contacts={contacts} sendNextDrip={sendNextDrip} />
        )}
        {activeTab === "automation" && (
          <AutomationTab automations={automations} toggleAuto={toggleAuto} />
        )}
        {activeTab === "templates" && (
          <TemplatesTab
            templates={templates} mergeTags={mergeTags}
            searchTemplate={searchTemplate} setSearchTemplate={setSearchTemplate}
            previewTemplate={previewTemplate} setPreviewTemplate={setPreviewTemplate}
            editingTemplate={editingTemplate} setEditingTemplate={setEditingTemplate}
            handleSaveTemplate={handleSaveTemplate} handleDeleteTemplate={handleDeleteTemplate}
            saving={saving}
          />
        )}
        {activeTab === "countdown" && (
          <CountdownTab
            contacts={contacts} addOpen={addOpen} setAddOpen={setAddOpen}
            editing={editing} setEditing={setEditing}
            saveContact={saveContact} sendNextDrip={sendNextDrip}
            handleDeleteContact={handleDeleteContact} saving={saving}
          />
        )}
        {activeTab === "activity" && (
          <ActivityTab activity={activity} analytics={analytics} handleResend={handleResend} handleExportCSV={handleExportCSV} />
        )}
      </div>
    </div>
  );
}

/* ==================== DASHBOARD TAB ==================== */

function DashboardTab({ remaining, goals, updateGoal, contacts, activity, automations }) {
  const now = new Date();
  const aepStart = new Date(now.getFullYear() > 2026 || (now.getFullYear() === 2026 && now.getMonth() >= 9) ? 2026 : 2026, 9, 15);
  const daysUntilAEP = Math.max(0, Math.ceil((aepStart - now) / 86400000));
  const inAEP = now >= aepStart && now <= new Date(2026, 11, 7);

  // Revenue projections
  const newRevenue = goals.newEnrollments * 694;
  const renewalRevenue = (goals.bookSize - goals.newEnrollments) * 347;
  const totalRevenue = newRevenue + renewalRevenue;

  // Automation count
  const autoCount = Object.values(automations).filter(Boolean).length;

  return (
    <div className="space-y-5">
      {/* Hero Countdown */}
      <section className="bg-gradient-to-r from-blue-800 via-blue-700 to-blue-900 text-white rounded-2xl p-5 sm:p-6 shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="text-xs font-semibold text-blue-200 uppercase tracking-wide">
              {inAEP ? "AEP IS LIVE — Oct 15 – Dec 7" : "Next AEP Window"}
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">
              {inAEP ? "AEP Is Open — Enroll Now!" : `${daysUntilAEP} Days Until AEP`}
            </h2>
            <p className="mt-1 text-blue-200 text-sm">Oct 15 – Dec 7 · 54 days · Plans effective Jan 1, 2027</p>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[["d", remaining.d], ["h", remaining.h], ["m", remaining.m], ["s", remaining.s]].map(([lbl, val]) => (
              <div key={lbl} className="bg-white/10 rounded-xl px-3 py-2 text-center border border-white/20">
                <div className="text-2xl font-black">{String(val).padStart(2, "0")}</div>
                <div className="text-xs text-blue-200 uppercase">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Progress tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <MetricTile label="Contacts in Pipeline" value={contacts.length} icon={Users} />
          <MetricTile label="Automations Active" value={autoCount} icon={Zap} />
          <MetricTile label="Sent This Month" value={activity.filter(a => a.status === "sent" || a.status === "delivered").length} icon={Send} />
          <MetricTile label="Days Until AEP" value={daysUntilAEP} icon={CalendarDays} />
        </div>
      </section>

      {/* Goals + Revenue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
          <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
            <Target className="text-blue-700" size={20} /> AEP Goals
          </h3>
          <div className="space-y-4">
            <GoalInput label="Appointment Goal" value={goals.appointments} onChange={v => updateGoal("appointments", v)}
              sub={`~${(goals.appointments / 54).toFixed(1)} per day across 54-day AEP`} />
            <GoalInput label="New Enrollments" value={goals.newEnrollments} onChange={v => updateGoal("newEnrollments", v)}
              sub={`${Math.round((goals.newEnrollments / goals.appointments) * 100)}% close rate needed`} />
            <GoalInput label="Book of Business Size" value={goals.bookSize} onChange={v => updateGoal("bookSize", v)}
              sub="Total existing clients to review" />
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
          <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
            <DollarSign className="text-green-600" size={20} /> Revenue Projection
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <div>
                <div className="font-semibold text-gray-800">New Enrollments</div>
                <div className="text-xs text-gray-500">{goals.newEnrollments} × $694/yr (MA commission)</div>
              </div>
              <div className="text-xl font-black text-green-700">${newRevenue.toLocaleString()}</div>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <div>
                <div className="font-semibold text-gray-800">Renewals Retained</div>
                <div className="text-xs text-gray-500">{Math.max(0, goals.bookSize - goals.newEnrollments)} × $347/yr</div>
              </div>
              <div className="text-xl font-black text-blue-700">${renewalRevenue.toLocaleString()}</div>
            </div>
            <div className="flex justify-between items-center py-2">
              <div className="font-bold text-gray-900">Total AEP Revenue</div>
              <div className="text-2xl font-black text-blue-900">${totalRevenue.toLocaleString()}</div>
            </div>
            <p className="text-xs text-gray-400 pt-1">Based on 2026 CMS MA commission rates. Actual commissions vary by carrier and state.</p>
          </div>
        </section>
      </div>

      {/* Key Dates Timeline */}
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
          <CalendarDays className="text-blue-700" size={20} /> AEP Key Dates — 2026/2027 Cycle
        </h3>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-blue-100 hidden sm:block" />
          <div className="space-y-3">
            {AEP_KEY_DATES.map((item, i) => {
              const colors = {
                blue: "bg-blue-100 text-blue-800 border-blue-200",
                yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
                green: "bg-green-100 text-green-800 border-green-200",
                red: "bg-red-100 text-red-800 border-red-200",
                purple: "bg-purple-100 text-purple-800 border-purple-200",
              };
              const dotColors = {
                blue: "bg-blue-500", yellow: "bg-yellow-500", green: "bg-green-500",
                red: "bg-red-500", purple: "bg-purple-500",
              };
              return (
                <div key={i} className="flex gap-4 sm:pl-8 relative">
                  <div className={`hidden sm:flex absolute left-0 w-8 h-8 rounded-full items-center justify-center ${dotColors[item.color]} text-white text-xs font-bold`}>
                    {i + 1}
                  </div>
                  <div className={`flex-1 rounded-xl border p-3 ${colors[item.color]}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-sm">{item.label}</span>
                      <span className="text-xs font-semibold bg-white/60 px-2 py-0.5 rounded-full">{item.date}</span>
                    </div>
                    <p className="text-xs mt-0.5 opacity-80">{item.note}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Tools Reference */}
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
          <ExternalLink className="text-blue-700" size={20} /> Essential AEP Tools
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {[
            { name: "Sunfire Matrix", desc: "MAPD/PDP quote + enroll", url: "https://www.sunfireinc.com", color: "blue" },
            { name: "Connecture", desc: "Plan comparison + enrollment", url: "https://www.connecture.com", color: "blue" },
            { name: "MedicareCENTER", desc: "Integrity all-in-one hub", url: "https://www.medicarecenter.com", color: "indigo" },
            { name: "CSG Actuarial", desc: "Medigap rate comparison", url: "https://www.csgactuarial.com", color: "purple" },
            { name: "Q1Medicare", desc: "Plan research + ANOC changes", url: "https://www.q1medicare.com", color: "green" },
            { name: "Medicare.gov Plan Finder", desc: "Official CMS comparison", url: "https://www.medicare.gov/plan-compare", color: "green" },
            { name: "SSA Extra Help", desc: "LIS/Low Income Subsidy", url: "https://www.ssa.gov/extrahelp", color: "orange" },
            { name: "AHIP Certification", desc: "$175 · 50q · 90% to pass", url: "https://www.ahip.org/education/medicare", color: "yellow" },
          ].map(tool => (
            <a
              key={tool.name}
              href={tool.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col gap-1 p-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition group"
            >
              <div className="font-semibold text-gray-900 text-sm group-hover:text-blue-700 flex items-center gap-1">
                {tool.name}
                <ExternalLink size={11} className="text-gray-400 group-hover:text-blue-500" />
              </div>
              <div className="text-xs text-gray-500">{tool.desc}</div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

function MetricTile({ label, value, icon: Icon }) {
  return (
    <div className="bg-white/10 rounded-xl border border-white/20 p-3 flex flex-col">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className="text-blue-200" />
        <div className="text-xs text-blue-200">{label}</div>
      </div>
      <div className="text-2xl font-black">{value}</div>
    </div>
  );
}

function GoalInput({ label, value, onChange, sub }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-semibold text-gray-700">{label}</label>
        <input
          type="number"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-24 text-right border border-gray-200 rounded-lg px-2 py-1 text-sm font-bold focus:border-blue-400 focus:outline-none"
          min="0"
        />
      </div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

/* ==================== COMPLIANCE TAB ==================== */

function ComplianceTab({ preCallChecks, setPreCallChecks }) {
  const [copiedDisclaimer, copyClaimer] = useCopyToClipboard();
  const [expandedRule, setExpandedRule] = useState(null);

  const allChecked = Object.values(preCallChecks).every(Boolean);

  return (
    <div className="space-y-5">
      {/* Pre-Call Checklist */}
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <h3 className="text-lg font-bold text-blue-900 mb-1 flex items-center gap-2">
          <CheckCircle2 className="text-green-600" size={20} /> Pre-Call Compliance Checklist
        </h3>
        <p className="text-xs text-gray-500 mb-4">Check off before every scheduled Medicare sales appointment.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { key: "ptc", label: "Permission to Contact (PTC) obtained", sub: "Valid for 12 months" },
            { key: "soa", label: "Scope of Appointment signed", sub: "Note: 48-hr wait eliminated Oct 1, 2026" },
            { key: "recording", label: "Call recording started", sub: "Required for ALL phone/virtual sales calls" },
            { key: "tpmo", label: "TPMO disclaimer ready", sub: "Must be delivered in first 60 seconds" },
          ].map(item => (
            <button
              key={item.key}
              onClick={() => setPreCallChecks(p => ({ ...p, [item.key]: !p[item.key] }))}
              className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition ${
                preCallChecks[item.key]
                  ? "border-green-300 bg-green-50"
                  : "border-gray-200 bg-white hover:border-blue-200"
              }`}
            >
              <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center ${
                preCallChecks[item.key] ? "bg-green-500 border-green-500" : "border-gray-300"
              }`}>
                {preCallChecks[item.key] && <Check size={12} className="text-white" />}
              </div>
              <div>
                <div className={`font-semibold text-sm ${preCallChecks[item.key] ? "text-green-800 line-through" : "text-gray-800"}`}>
                  {item.label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{item.sub}</div>
              </div>
            </button>
          ))}
        </div>
        {allChecked && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2 text-green-800 font-semibold text-sm">
            <CheckCircle2 size={18} /> All checks complete — you're ready to begin!
          </div>
        )}
        <button
          onClick={() => setPreCallChecks({ ptc: false, soa: false, recording: false, tpmo: false })}
          className="mt-3 text-xs text-gray-400 hover:text-gray-600 underline"
        >
          Reset all
        </button>
      </section>

      {/* TPMO Disclaimer */}
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
              <FileText className="text-blue-700" size={20} /> TPMO Disclaimer (Required Verbatim)
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Say this in the first 60 seconds of every sales call. Required on all marketing materials.</p>
          </div>
          <button
            onClick={() => copyClaimer(TPMO_DISCLAIMER.replace(/"/g, ""))}
            className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition ${
              copiedDisclaimer ? "bg-green-100 text-green-700" : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {copiedDisclaimer ? <Check size={16} /> : <Copy size={16} />}
            {copiedDisclaimer ? "Copied!" : "Copy"}
          </button>
        </div>
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-4 font-mono text-sm text-blue-900 leading-relaxed">
          {TPMO_DISCLAIMER}
        </div>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
          Replace [X] with the actual number of organizations and products you represent in the beneficiary's service area.
          Also required: Language Assistance availability notice (as of Sep 30, 2025).
        </p>
      </section>

      {/* Compliance Rules */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {COMPLIANCE_RULES.map((rule, i) => {
          const Icon = rule.icon;
          const isOpen = expandedRule === i;
          const colors = {
            blue: "bg-blue-50 border-blue-200 text-blue-900",
            red: "bg-red-50 border-red-200 text-red-900",
            purple: "bg-purple-50 border-purple-200 text-purple-900",
            orange: "bg-orange-50 border-orange-200 text-orange-900",
            teal: "bg-teal-50 border-teal-200 text-teal-900",
            gray: "bg-gray-50 border-gray-200 text-gray-900",
          };
          const iconColors = {
            blue: "text-blue-600", red: "text-red-600", purple: "text-purple-600",
            orange: "text-orange-600", teal: "text-teal-600", gray: "text-gray-600",
          };
          return (
            <div key={i} className={`rounded-xl border ${colors[rule.color]} shadow-sm`}>
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => setExpandedRule(isOpen ? null : i)}
              >
                <div className="flex items-center gap-3">
                  <Icon className={iconColors[rule.color]} size={20} />
                  <div>
                    <div className="font-bold text-sm">{rule.title}</div>
                    {rule.badge && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mt-0.5 inline-block ${
                        rule.badgeColor === "green" ? "bg-green-200 text-green-800" : "bg-blue-200 text-blue-800"
                      }`}>
                        {rule.badge}
                      </span>
                    )}
                  </div>
                </div>
                {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {isOpen && (
                <div className="px-4 pb-4">
                  <ul className="space-y-2">
                    {rule.rules.map((r, j) => (
                      <li key={j} className="flex gap-2 text-sm">
                        <span className="text-gray-400 mt-0.5">•</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Penalty Quick Reference */}
      <section className="bg-white rounded-2xl shadow p-5 border border-red-100">
        <h3 className="text-lg font-bold text-red-900 mb-3 flex items-center gap-2">
          <AlertCircle className="text-red-600" size={20} /> Penalty Quick Reference
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { title: "TCPA Violations", detail: "$500–$1,500 per unsolicited call/text", severity: "high" },
            { title: "Gift Rule Violation", detail: "Max $15/item, $75/year per person. No cash.", severity: "medium" },
            { title: "Non-RTS Selling", detail: "Contract termination + possible license revocation", severity: "critical" },
          ].map((item, i) => (
            <div key={i} className={`rounded-xl p-4 border-2 ${
              item.severity === "critical" ? "border-red-400 bg-red-50" :
              item.severity === "high" ? "border-orange-300 bg-orange-50" : "border-yellow-300 bg-yellow-50"
            }`}>
              <div className="font-bold text-sm text-gray-900">{item.title}</div>
              <div className="text-xs text-gray-700 mt-1">{item.detail}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ==================== CERTIFICATIONS TAB ==================== */

function CertificationsTab({ certs, updateCert }) {
  const ahip = certs.ahip || {};
  const ahipDone = ahip.status === "completed";

  return (
    <div className="space-y-5">
      {/* AHIP Card */}
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-black ${
            ahipDone ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
          }`}>
            {ahipDone ? "✓" : "AHIP"}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-bold text-blue-900">AHIP Medicare Certification</h3>
              <StatusBadge status={ahip.status || "not_started"} />
            </div>
            <p className="text-sm text-gray-500 mt-0.5">Required annually before carrier-specific certifications. Opens ~June 2026.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
              <InfoChip label="Cost" value="$175" sub="($50 off via carrier portal)" />
              <InfoChip label="Questions" value="50" sub="2-hour time limit" />
              <InfoChip label="Pass Score" value="90%" sub="45 of 50 correct" />
              <InfoChip label="Validity" value="1 Year" sub="Must renew annually" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-100">
          <div>
            <label className="text-xs font-semibold text-gray-600">Status</label>
            <select
              value={ahip.status || "not_started"}
              onChange={e => updateCert("ahip", "status", e.target.value)}
              className="block w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed ✓</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Certificate #</label>
            <input
              type="text"
              value={ahip.certNumber || ""}
              onChange={e => updateCert("ahip", "certNumber", e.target.value)}
              placeholder="e.g., AHIP-2026-XXXXXX"
              className="block w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Date Completed</label>
            <input
              type="date"
              value={ahip.completedDate || ""}
              onChange={e => updateCert("ahip", "completedDate", e.target.value)}
              className="block w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="mt-3">
          <a
            href="https://www.ahip.org/education/medicare"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-blue-600 font-semibold hover:underline"
          >
            <ExternalLink size={14} /> Go to AHIP.org to certify
          </a>
        </div>
      </section>

      {/* Carrier Certifications */}
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <h3 className="text-lg font-bold text-blue-900 mb-1 flex items-center gap-2">
          <Award className="text-blue-700" size={20} /> Carrier Certifications
        </h3>
        <p className="text-sm text-gray-500 mb-4">Must be RTS (Ready to Sell) with all carriers before October 1.</p>

        {/* RTS progress */}
        {(() => {
          const total = CARRIERS.length;
          const done = CARRIERS.filter(c => (certs[c.key]?.status || "") === "rts").length;
          const pct = Math.round((done / total) * 100);
          return (
            <div className="mb-5">
              <div className="flex justify-between text-sm font-semibold mb-1">
                <span>Ready-to-Sell Progress</span>
                <span className="text-blue-700">{done}/{total} carriers</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })()}

        <div className="space-y-3">
          {CARRIERS.map(carrier => {
            const c = certs[carrier.key] || {};
            return (
              <div key={carrier.key} className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-gray-900">{carrier.name}</span>
                      <StatusBadge status={c.status || "not_started"} small />
                      <span className="text-xs text-gray-400">Opens {carrier.opens}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{carrier.notes}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                  <div>
                    <label className="text-xs text-gray-500">Status</label>
                    <select
                      value={c.status || "not_started"}
                      onChange={e => updateCert(carrier.key, "status", e.target.value)}
                      className="block w-full mt-0.5 border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                    >
                      <option value="not_started">Not Started</option>
                      <option value="in_progress">In Progress</option>
                      <option value="certified">Certified</option>
                      <option value="rts">RTS ✓</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Writing Code / NPN</label>
                    <input
                      type="text"
                      value={c.writingCode || ""}
                      onChange={e => updateCert(carrier.key, "writingCode", e.target.value)}
                      placeholder="Writing code"
                      className="block w-full mt-0.5 border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Certified Date</label>
                    <input
                      type="date"
                      value={c.certDate || ""}
                      onChange={e => updateCert(carrier.key, "certDate", e.target.value)}
                      className="block w-full mt-0.5 border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">RTS Date</label>
                    <input
                      type="date"
                      value={c.rtsDate || ""}
                      onChange={e => updateCert(carrier.key, "rtsDate", e.target.value)}
                      className="block w-full mt-0.5 border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 90-Day Timeline */}
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
          <Clock className="text-blue-700" size={20} /> 90-Day Pre-AEP Certification Timeline
        </h3>
        <div className="space-y-3">
          {[
            { period: "June 2026", phase: "Foundation", color: "blue", actions: ["Complete AHIP certification ($175, 90% pass required)", "Confirm E&O policy active through Dec 31", "Verify all state licenses — renew if needed", "Review carrier portfolio — decide which carriers to certify"] },
            { period: "July 2026", phase: "Carrier Certs", color: "blue", actions: ["Begin carrier certifications as portals open (UHC, Humana, Cigna open July)", "Complete each carrier's product modules and assessments", "Verify RTS status in each carrier portal", "Submit contracting requests for any new carriers by August"] },
            { period: "September 2026", phase: "Confirmation", color: "yellow", actions: ["Confirm RTS status with EVERY carrier you sell", "Complete any new-product supplemental certifications", "Review new 2027 plan benefits (carriers release in September)", "Update SOA forms and marketing materials with approved language"] },
            { period: "October 1, 2026", phase: "AEP Marketing Opens", color: "green", actions: ["CY2027 rule changes take effect (48-hr SOA wait eliminated)", "First day to hold sales events and send AEP marketing", "Begin outreach to existing book of business", "All RTS statuses must be confirmed"] },
            { period: "October 15, 2026", phase: "AEP Opens", color: "green", actions: ["First day to accept AEP applications for Jan 1 effective date", "All certifications must be complete before writing any application", "Execute full outreach sequence"] },
          ].map((item, i) => (
            <div key={i} className={`flex gap-4 items-start p-4 rounded-xl border ${
              item.color === "green" ? "bg-green-50 border-green-200" :
              item.color === "yellow" ? "bg-yellow-50 border-yellow-200" :
              "bg-blue-50 border-blue-200"
            }`}>
              <div className="flex-shrink-0">
                <div className="text-xs font-black text-gray-700">{item.period}</div>
                <div className="text-xs text-gray-500">{item.phase}</div>
              </div>
              <ul className="space-y-1 flex-1">
                {item.actions.map((a, j) => (
                  <li key={j} className="text-xs text-gray-700 flex gap-2">
                    <span className="text-gray-400 mt-0.5">•</span><span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ status, small }) {
  const configs = {
    not_started: { label: "Not Started", color: "bg-gray-100 text-gray-600" },
    in_progress: { label: "In Progress", color: "bg-yellow-100 text-yellow-700" },
    completed: { label: "Completed ✓", color: "bg-green-100 text-green-700" },
    certified: { label: "Certified", color: "bg-blue-100 text-blue-700" },
    rts: { label: "RTS ✓", color: "bg-green-200 text-green-800 font-bold" },
  };
  const { label, color } = configs[status] || configs.not_started;
  return (
    <span className={`rounded-full ${small ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-xs"} font-semibold ${color}`}>
      {label}
    </span>
  );
}

function InfoChip({ label, value, sub }) {
  return (
    <div className="bg-blue-50 rounded-xl p-2.5 border border-blue-100 text-center">
      <div className="text-xs text-blue-500 font-semibold">{label}</div>
      <div className="font-black text-blue-900 text-base">{value}</div>
      {sub && <div className="text-xs text-blue-400 mt-0.5">{sub}</div>}
    </div>
  );
}

/* ==================== ANOC CENTER TAB ==================== */

function AnocTab({ contacts, sendNextDrip }) {
  const [copied, setCopied] = useState(null);
  const [expandedPhase, setExpandedPhase] = useState(0);

  const atRisk = contacts.filter(c => c.status !== "Enrolled" && c.status !== "Not Interested");

  function copyTemplate(idx) {
    const tpl = ANOC_SEQUENCE[idx];
    navigator.clipboard?.writeText(tpl.template);
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div className="space-y-5">
      {/* What is ANOC */}
      <section className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl border border-amber-200 p-5">
        <h3 className="text-lg font-bold text-amber-900 mb-2 flex items-center gap-2">
          <Info className="text-amber-600" size={20} /> What Is the ANOC?
        </h3>
        <p className="text-sm text-amber-800 leading-relaxed">
          The <strong>Annual Notice of Change (ANOC)</strong> is a required document every Medicare Advantage and Part D plan must mail to enrollees
          by <strong>September 30</strong>. It shows all changes to the plan for the coming year: premiums, copays, drug formulary changes,
          network changes, and benefit additions/reductions. When clients receive their ANOC, it triggers your most important outreach window.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          {[
            { label: "ANOC Mailed By", value: "Sept 30", color: "amber" },
            { label: "MA Enrollees Affected (2026)", value: "~10%", color: "red", note: "Lost their plan" },
            { label: "Monday Response Rate", value: "2× Avg", color: "green", note: "Best outreach day" },
          ].map((s, i) => (
            <div key={i} className="bg-white/80 rounded-xl p-3 text-center border border-amber-100">
              <div className="text-xs font-semibold text-amber-600">{s.label}</div>
              <div className="text-2xl font-black text-amber-900">{s.value}</div>
              {s.note && <div className="text-xs text-amber-500">{s.note}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* At-Risk Client Segmentation Guide */}
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
          <Target className="text-blue-700" size={20} /> Client Priority Segmentation
        </h3>
        <div className="space-y-3">
          {[
            {
              tier: "Tier 1 — Immediate Action",
              color: "red",
              clients: "Plan discontinued, exiting market, or major benefit loss",
              triggers: ["Plan exits the market (no choice — must re-enroll)", "Plan drops key PCP or hospital from network", "Star rating drops below 3 stars"],
              action: "Call within 24 hours of ANOC delivery.",
            },
            {
              tier: "Tier 2 — High Priority",
              color: "orange",
              clients: "Significant plan changes",
              triggers: ["Premium increases $30+/month", "Drug moved to higher tier or removed from formulary", "MOOP increases significantly", "OTC/dental/vision benefits reduced"],
              action: "Contact within Day 1-7 of ANOC window.",
            },
            {
              tier: "Tier 3 — Courtesy Review",
              color: "green",
              clients: "Minimal or no changes",
              triggers: ["Plan stable — premium flat or minimal increase", "Drugs still covered on same tier", "All providers still in-network"],
              action: "Annual review call — confirm all good. Protect from competitor outreach.",
            },
          ].map((seg, i) => (
            <div key={i} className={`rounded-xl border-2 p-4 ${
              seg.color === "red" ? "border-red-300 bg-red-50" :
              seg.color === "orange" ? "border-orange-300 bg-orange-50" :
              "border-green-300 bg-green-50"
            }`}>
              <div className={`font-bold text-sm mb-1 ${
                seg.color === "red" ? "text-red-800" :
                seg.color === "orange" ? "text-orange-800" : "text-green-800"
              }`}>{seg.tier}</div>
              <div className="text-xs text-gray-600 mb-2">{seg.clients}</div>
              <ul className="space-y-1 mb-2">
                {seg.triggers.map((t, j) => (
                  <li key={j} className="text-xs text-gray-700 flex gap-2">
                    <span className="text-gray-400">•</span><span>{t}</span>
                  </li>
                ))}
              </ul>
              <div className={`text-xs font-semibold px-2 py-1 rounded-lg inline-block ${
                seg.color === "red" ? "bg-red-200 text-red-800" :
                seg.color === "orange" ? "bg-orange-200 text-orange-800" : "bg-green-200 text-green-800"
              }`}>{seg.action}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ANOC Outreach Sequence */}
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
          <Activity className="text-blue-700" size={20} /> ANOC Outreach Sequence
        </h3>
        <div className="space-y-3">
          {ANOC_SEQUENCE.map((phase, i) => {
            const Icon = phase.icon;
            const isOpen = expandedPhase === i;
            const colors = { blue: "border-blue-300 bg-blue-50", yellow: "border-yellow-300 bg-yellow-50", orange: "border-orange-300 bg-orange-50", red: "border-red-300 bg-red-50" };
            const textColors = { blue: "text-blue-800", yellow: "text-yellow-800", orange: "text-orange-800", red: "text-red-800" };
            return (
              <div key={i} className={`rounded-xl border-2 ${colors[phase.color]}`}>
                <button
                  className="w-full flex items-center justify-between p-4 text-left"
                  onClick={() => setExpandedPhase(isOpen ? null : i)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      phase.color === "blue" ? "bg-blue-100" :
                      phase.color === "yellow" ? "bg-yellow-100" :
                      phase.color === "orange" ? "bg-orange-100" : "bg-red-100"
                    }`}>
                      <Icon size={20} className={textColors[phase.color]} />
                    </div>
                    <div>
                      <div className={`font-bold text-sm ${textColors[phase.color]}`}>{phase.phase}: {phase.title}</div>
                      <div className="text-xs text-gray-500">{phase.channel}</div>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4">
                    <div className="bg-white/80 rounded-xl p-3 border border-gray-200 text-sm text-gray-700 font-mono leading-relaxed">
                      {phase.template}
                    </div>
                    <button
                      onClick={() => copyTemplate(i)}
                      className={`mt-2 flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg font-semibold transition ${
                        copied === i ? "bg-green-100 text-green-700" : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {copied === i ? <Check size={14} /> : <Copy size={14} />}
                      {copied === i ? "Copied!" : "Copy Template"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Countdown List Quick View */}
      {atRisk.length > 0 && (
        <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
          <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
            <Users className="text-blue-700" size={20} /> Countdown List — Pending Contacts ({atRisk.length})
          </h3>
          <div className="divide-y max-h-72 overflow-y-auto">
            {atRisk.slice(0, 15).map(c => (
              <div key={c.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-semibold text-gray-900 text-sm">{c.firstName} {c.lastName}</div>
                  <div className="text-xs text-gray-500">{c.phone || c.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    c.status === "Warm" ? "bg-yellow-100 text-yellow-700" :
                    c.status === "Scheduled" ? "bg-blue-100 text-blue-700" :
                    "bg-gray-100 text-gray-600"
                  }`}>{c.status}</span>
                  <button
                    onClick={() => sendNextDrip(c)}
                    className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-blue-700"
                  >
                    Send Drip
                  </button>
                </div>
              </div>
            ))}
          </div>
          {atRisk.length > 15 && (
            <p className="text-xs text-gray-400 mt-2 text-center">Showing 15 of {atRisk.length}. See full list in Countdown List tab.</p>
          )}
        </section>
      )}
    </div>
  );
}

/* ==================== AUTOMATION TAB ==================== */

function AutomationTab({ automations, toggleAuto }) {
  const autoConfigs = [
    { key: "preAEP60", label: "60-Day Pre-AEP Email", desc: "Email blast to all clients 60 days before Oct 15", timing: "~Aug 15" },
    { key: "preAEP30", label: "30-Day Pre-AEP Email", desc: "Second email with AEP information", timing: "~Sep 15" },
    { key: "preAEP14", label: "14-Day Pre-AEP Email", desc: "Email reminder with booking link", timing: "~Oct 1" },
    { key: "preAEP7", label: "7-Day Pre-AEP SMS", desc: "SMS reminder — high open rate", timing: "~Oct 8" },
    { key: "preAEP3", label: "3-Day Reminder", desc: "Last reminder before AEP opens", timing: "~Oct 12" },
    { key: "preAEP1", label: "1-Day Final Push", desc: "AEP opens tomorrow message", timing: "Oct 14" },
    { key: "anocExplainer", label: "ANOC Explainer", desc: "Sends ANOC explanation to clients who got the letter", timing: "Late Sep" },
    { key: "bookingNudges", label: "Booking Nudges", desc: "Automated reminders to book annual review", timing: "Oct-Nov" },
    { key: "voicemailDropUI", label: "Voicemail Drop UI", desc: "Shows voicemail drop option in client actions", timing: "Always" },
    { key: "requireApproval", label: "Require Approval", desc: "Automations queue for your review before sending", timing: "Always" },
  ];

  const activeCount = autoConfigs.filter(a => automations[a.key]).length;

  return (
    <div className="space-y-5">
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
              <Zap className="text-blue-700" size={20} /> Outreach Automations
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">{activeCount} of {autoConfigs.length} automations active</p>
          </div>
          <div className="h-10 w-10 rounded-full border-4 border-blue-200 flex items-center justify-center text-xs font-black text-blue-700">
            {activeCount}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {autoConfigs.map(cfg => (
            <button
              key={cfg.key}
              onClick={() => toggleAuto(cfg.key)}
              className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition ${
                automations[cfg.key]
                  ? "border-blue-300 bg-blue-50"
                  : "border-gray-200 bg-white hover:border-blue-200"
              }`}
            >
              <div className={`flex-shrink-0 mt-0.5 w-10 h-6 rounded-full flex items-center transition-colors ${
                automations[cfg.key] ? "bg-blue-600 justify-end" : "bg-gray-200 justify-start"
              } px-0.5`}>
                <div className="w-5 h-5 bg-white rounded-full shadow" />
              </div>
              <div>
                <div className="font-semibold text-gray-900 text-sm">{cfg.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{cfg.desc}</div>
                <div className="text-xs text-blue-500 mt-0.5 font-semibold">Timing: {cfg.timing}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <Info size={16} className="inline mr-2 -mt-0.5" />
        <strong>Compliance note:</strong> All automations require valid PTC from each recipient. SMS automations require TCPA-compliant opt-in.
        Enable "Require Approval" to review all messages before they send.
      </div>
    </div>
  );
}

/* ==================== TEMPLATES TAB ==================== */

function TemplatesTab({
  templates, mergeTags, searchTemplate, setSearchTemplate,
  previewTemplate, setPreviewTemplate, editingTemplate, setEditingTemplate,
  handleSaveTemplate, handleDeleteTemplate, saving,
}) {
  function handleInsertTemplate(tpl) {
    setPreviewTemplate(null);
    navigator.clipboard?.writeText(tpl.content);
    alert("Template copied to clipboard!");
  }

  return (
    <div className="space-y-5">
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
            <Star className="text-yellow-500" size={20} /> Message Templates
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditingTemplate({ title: "", type: "email", subject: "", content: "", tags: [] })}
              className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center gap-1"
            >
              <Plus size={14} /> New Template
            </button>
            <input
              type="text"
              className="rounded-lg border px-3 py-1.5 text-sm w-40"
              placeholder="Search..."
              value={searchTemplate}
              onChange={e => setSearchTemplate(e.target.value)}
            />
          </div>
        </div>

        {templates.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Star size={40} className="mx-auto mb-3 opacity-30" />
            <p>No templates yet. Create your first AEP message template.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates
              .filter(t => (t.title || "").toLowerCase().includes(searchTemplate.toLowerCase()) || (t.tags || []).join(" ").toLowerCase().includes(searchTemplate.toLowerCase()))
              .map(tpl => (
                <div key={tpl.id} className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 bg-gray-50 transition">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-900 text-sm truncate">
                        {tpl.title}
                        <span className="text-xs text-gray-400 ml-1 font-normal">({tpl.type})</span>
                        {tpl.is_system && <span className="text-xs text-gray-400 ml-1">(System)</span>}
                      </div>
                      {tpl.subject && <div className="text-xs text-gray-500 mt-0.5 truncate">{tpl.subject}</div>}
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {(tpl.tags || []).map((tag, j) => (
                          <span key={j} className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">{tag}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg" onClick={() => setPreviewTemplate(tpl)} title="Preview"><EyeIcon size={15} /></button>
                      <button className="text-green-600 hover:bg-green-50 p-1.5 rounded-lg" onClick={() => setEditingTemplate({ ...tpl, subject: tpl.subject || "", tags: tpl.tags || [] })} title="Edit"><Edit2 size={15} /></button>
                      {!tpl.is_system && (
                        <button className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg" onClick={() => handleDeleteTemplate(tpl)} title="Delete"><Trash2 size={15} /></button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </section>

      {/* Preview Modal */}
      {previewTemplate && (
        <Modal onClose={() => setPreviewTemplate(null)} title={`Preview: ${previewTemplate.title}`}>
          <pre className="bg-blue-50 rounded-xl p-4 whitespace-pre-wrap text-sm text-gray-900 mb-4 border border-blue-100">{previewTemplate.content}</pre>
          {mergeTags.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-500 mb-2">With Sample Data:</div>
              <pre className="bg-green-50 rounded-xl p-4 whitespace-pre-wrap text-sm text-green-900 border border-green-100">
                {mergeTags.reduce((acc, tag) => acc.replace(new RegExp(tag.tag.replace(/[{}]/g, "\\$&"), "g"), tag.description || tag.key), previewTemplate.content)}
              </pre>
            </div>
          )}
          <div className="flex gap-2">
            <button className="bg-blue-700 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-blue-800" onClick={() => handleInsertTemplate(previewTemplate)}>
              <Copy size={14} className="inline mr-1 -mt-0.5" /> Copy to Clipboard
            </button>
          </div>
          {mergeTags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {mergeTags.map((tag, i) => (
                <span key={i} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-mono">{tag.tag}</span>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* Edit Modal */}
      {editingTemplate && (
        <Modal onClose={() => setEditingTemplate(null)} title={editingTemplate.id ? "Edit Template" : "New Template"}>
          <form onSubmit={handleSaveTemplate} className="space-y-4">
            {editingTemplate.is_system && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded-lg text-sm">
                This is a system template. Saving will create your personal copy.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-gray-700">Title *</label>
                <input type="text" className="block w-full border rounded-lg p-2 mt-1" value={editingTemplate.title}
                  onChange={e => setEditingTemplate(t => ({ ...t, title: e.target.value }))} required />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Type *</label>
                <select className="block w-full border rounded-lg p-2 mt-1" value={editingTemplate.type}
                  onChange={e => setEditingTemplate(t => ({ ...t, type: e.target.value }))}>
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
            </div>
            {editingTemplate.type === "email" && (
              <div>
                <label className="text-sm font-semibold text-gray-700">Subject Line</label>
                <input type="text" className="block w-full border rounded-lg p-2 mt-1" value={editingTemplate.subject}
                  onChange={e => setEditingTemplate(t => ({ ...t, subject: e.target.value }))} placeholder="e.g., AEP is coming — let's prepare!" />
              </div>
            )}
            <div>
              <label className="text-sm font-semibold text-gray-700">Content *</label>
              <textarea className="block w-full border rounded-lg p-2 mt-1 font-mono text-sm" rows={8}
                value={editingTemplate.content} onChange={e => setEditingTemplate(t => ({ ...t, content: e.target.value }))}
                required placeholder={editingTemplate.type === "sms" ? "Hi {ClientName}, AEP starts Oct 15..." : "Hi {ClientName},\n\nThe Medicare Annual Enrollment Period starts October 15..."} />
              {mergeTags.length > 0 && (
                <div className="text-xs text-gray-400 mt-1">Tags: {mergeTags.map(t => t.tag).join(", ")}</div>
              )}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">Tags (comma separated)</label>
              <input type="text" className="block w-full border rounded-lg p-2 mt-1"
                value={(editingTemplate.tags || []).join(", ")}
                onChange={e => setEditingTemplate(t => ({ ...t, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))}
                placeholder="Pre-AEP, Email, Reminder" />
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={saving} className="bg-blue-700 text-white px-5 py-2 rounded-xl font-bold hover:bg-blue-800 disabled:opacity-50 flex items-center gap-2">
                <Save size={16} /> {saving ? "Saving..." : editingTemplate.is_system ? "Save as Copy" : "Save Template"}
              </button>
              <button type="button" onClick={() => setEditingTemplate(null)} className="bg-gray-100 text-gray-700 px-5 py-2 rounded-xl hover:bg-gray-200">
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ==================== COUNTDOWN LIST TAB ==================== */

function CountdownTab({ contacts, addOpen, setAddOpen, editing, setEditing, saveContact, sendNextDrip, handleDeleteContact, saving }) {
  const statusColor = {
    New: "bg-gray-100 text-gray-700",
    Warm: "bg-yellow-100 text-yellow-800",
    Scheduled: "bg-blue-100 text-blue-800",
    Enrolled: "bg-green-100 text-green-800",
    "Not Interested": "bg-red-100 text-red-700",
  };

  return (
    <div className="space-y-4">
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
              <Users className="text-blue-700" size={20} /> AEP Countdown List
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">Prospects waiting for AEP — {contacts.length} total</p>
          </div>
          <button
            onClick={() => { setEditing(null); setAddOpen(true); }}
            className="bg-blue-800 text-white px-4 py-2 rounded-xl font-bold shadow hover:bg-blue-900 text-sm flex items-center gap-2"
          >
            <UserPlus size={16} /> Add Contact
          </button>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b text-xs">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Phone / Email</th>
                <th className="py-2 pr-4">ZIP</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Outreach Plan</th>
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-2 pr-4 font-semibold text-gray-900">{c.firstName} {c.lastName}</td>
                  <td className="py-2 pr-4 text-gray-600">{c.phone || c.email}</td>
                  <td className="py-2 pr-4 text-gray-500">{c.zip || "—"}</td>
                  <td className="py-2 pr-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor[c.status] || statusColor.New}`}>{c.status}</span>
                  </td>
                  <td className="py-2 pr-4 text-xs text-gray-500">
                    2mo {c.outreachPlan?.twoMonths ? "•" : "×"} / 1mo {c.outreachPlan?.oneMonth ? "•" : "×"} / 2w {c.outreachPlan?.twoWeeks ? "•" : "×"} / 1w {c.outreachPlan?.oneWeek ? "•" : "×"}
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex gap-2">
                      <button className="text-blue-600 text-xs font-semibold hover:underline" onClick={() => sendNextDrip(c)}>Drip</button>
                      <button className="text-gray-600 text-xs hover:underline" onClick={() => { setEditing(c); setAddOpen(true); }}>Edit</button>
                      <button className="text-red-500 text-xs hover:underline" onClick={() => handleDeleteContact(c)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
              {contacts.length === 0 && (
                <tr><td className="py-8 text-gray-400 text-center" colSpan={6}>No contacts yet. Add prospects waiting for AEP!</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {contacts.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No contacts yet. Add prospects waiting for AEP!</p>
          ) : contacts.map(c => (
            <div key={c.id} className="border rounded-xl p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-semibold text-gray-900">{c.firstName} {c.lastName}</div>
                  <div className="text-sm text-gray-500">{c.phone || c.email}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statusColor[c.status] || statusColor.New}`}>{c.status}</span>
              </div>
              <div className="flex gap-3 pt-2 border-t">
                <button className="text-blue-600 text-sm font-semibold" onClick={() => sendNextDrip(c)}>Send Drip</button>
                <button className="text-gray-600 text-sm" onClick={() => { setEditing(c); setAddOpen(true); }}>Edit</button>
                <button className="text-red-500 text-sm" onClick={() => handleDeleteContact(c)}>Delete</button>
              </div>
            </div>
          ))}
        </div>

        {/* Add/Edit Modal */}
        {addOpen && (
          <Modal onClose={() => setAddOpen(false)} title={editing ? "Edit Contact" : "Add Contact"}>
            <form onSubmit={saveContact} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input name="firstName" label="First Name *" defaultValue={editing?.firstName} required />
                <Input name="lastName" label="Last Name *" defaultValue={editing?.lastName} required />
                <Input name="phone" label="Phone" defaultValue={editing?.phone} />
                <Input name="email" label="Email" defaultValue={editing?.email} />
                <Input name="zip" label="ZIP" defaultValue={editing?.zip} />
                <Input name="county" label="County" defaultValue={editing?.county} />
                <Input name="dob" type="date" label="Date of Birth" defaultValue={editing?.dob} />
                <Select name="language" label="Language" defaultValue={editing?.language ?? "English"} options={["English", "Spanish", "Other"]} />
                <Select name="source" label="Source" defaultValue={editing?.source ?? "Other"} options={["Event", "Referral", "Inbound", "Other"]} />
                <Select name="status" label="Status" defaultValue={editing?.status ?? "New"} options={["New", "Warm", "Scheduled", "Enrolled", "Not Interested"]} />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Notes</label>
                <textarea className="block w-full border rounded-lg p-2 mt-1" name="notes" defaultValue={editing?.notes} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Checkbox name="ptc" label="Permission to Contact (PTC)" defaultChecked={!!editing?.permissionToContact} />
                <Checkbox name="newsletter" label="Monthly Newsletter" defaultChecked={!!editing?.newsletter} />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-700 mb-2">Outreach Plan</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Checkbox name="twoMonths" label="60 Days" defaultChecked={editing?.outreachPlan?.twoMonths ?? true} />
                  <Checkbox name="oneMonth" label="30 Days" defaultChecked={editing?.outreachPlan?.oneMonth ?? true} />
                  <Checkbox name="twoWeeks" label="14 Days" defaultChecked={editing?.outreachPlan?.twoWeeks ?? true} />
                  <Checkbox name="oneWeek" label="7 Days" defaultChecked={editing?.outreachPlan?.oneWeek ?? true} />
                  <Checkbox name="aepLive" label="AEP Live" defaultChecked={editing?.outreachPlan?.aepLive ?? true} />
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={saving} className="bg-blue-800 text-white px-5 py-2 rounded-xl font-bold disabled:opacity-50">
                  {saving ? "Saving..." : editing ? "Save Changes" : "Add Contact"}
                </button>
                <button type="button" onClick={() => setAddOpen(false)} className="bg-gray-100 text-gray-700 px-5 py-2 rounded-xl hover:bg-gray-200">Cancel</button>
              </div>
            </form>
          </Modal>
        )}
      </section>
    </div>
  );
}

/* ==================== ACTIVITY LOG TAB ==================== */

function ActivityTab({ activity, analytics, handleResend, handleExportCSV }) {
  return (
    <div className="space-y-5">
      {/* Analytics */}
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
          <TrendingUp className="text-blue-700" size={20} /> AEP Outreach Analytics
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {(analytics.length > 0 ? analytics : [
            { labelKey: "automationsSent", value: 0 },
            { labelKey: "openRate", value: "0%" },
            { labelKey: "clickRate", value: "0%" },
            { labelKey: "replyRate", value: "0%" },
            { labelKey: "bounces", value: 0 },
            { labelKey: "failedSends", value: 0 },
          ]).map((a, i) => {
            const icons = { automationsSent: Send, openRate: Eye, clickRate: ArrowUpRight, replyRate: MessageSquare, bounces: AlertCircle, failedSends: XCircle };
            const Icon = icons[a.labelKey] || Send;
            return (
              <div key={i} className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
                <Icon className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                <div className="text-xl font-black text-blue-900">{a.value}</div>
                <div className="text-xs text-gray-500 capitalize">{a.labelKey.replace(/([A-Z])/g, " $1")}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Activity Feed */}
      <section className="bg-white rounded-2xl shadow p-5 border border-blue-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
            <ListChecks className="text-blue-700" size={20} /> Activity Feed
          </h3>
          <button className="flex items-center gap-1 text-sm text-blue-700 hover:underline font-semibold" onClick={handleExportCSV}>
            <Download size={16} /> Export CSV
          </button>
        </div>
        <div className="divide-y max-h-96 overflow-y-auto">
          {activity.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <Activity size={40} className="mx-auto mb-3 opacity-30" />
              <p>No activity yet. Send your first outreach!</p>
            </div>
          ) : activity.map((a, idx) => (
            <div key={a.id || idx} className="py-3 flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">{a.automation || "Manual"}</div>
                <div className="text-xs text-gray-500">{formatTime(a.time)} · {a.type} · {a.to}</div>
                {a.subject && a.subject !== "—" && <div className="text-xs text-gray-400 truncate">{a.subject}</div>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {(a.status === "delivered" || a.status === "sent") && (
                  <span className="text-green-600 flex items-center gap-1 text-xs"><CheckCircle2 size={14} /> Sent</span>
                )}
                {a.status === "opened" && (
                  <span className="text-blue-600 flex items-center gap-1 text-xs"><Eye size={14} /> Opened</span>
                )}
                {a.status === "queued" && (
                  <span className="text-yellow-600 flex items-center gap-1 text-xs"><RefreshCw size={14} /> Queued</span>
                )}
                {a.status === "failed" && (
                  <>
                    <span className="text-red-600 flex items-center gap-1 text-xs"><XCircle size={14} /> Failed</span>
                    <button className="text-blue-700 text-xs underline" onClick={() => handleResend(idx)}>Resend</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ==================== SHARED SMALL COMPONENTS ==================== */

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-3xl shadow-2xl relative max-h-[92vh] flex flex-col sm:m-4">
        <button className="absolute top-3 right-4 text-gray-400 text-2xl z-10 p-1 hover:text-gray-700" onClick={onClose}>&times;</button>
        <div className="p-5 sm:p-6 overflow-y-auto">
          {title && <h2 className="text-lg font-bold text-blue-900 mb-4 pr-8">{title}</h2>}
          {children}
        </div>
      </div>
    </div>
  );
}

function Input({ name, label, type = "text", defaultValue, required }) {
  return (
    <label className="text-sm font-semibold text-gray-700">
      {label}
      <input name={name} type={type} defaultValue={defaultValue} required={required}
        className="block w-full border rounded-lg p-2 mt-1 font-normal" />
    </label>
  );
}

function Select({ name, label, defaultValue, options = [] }) {
  return (
    <label className="text-sm font-semibold text-gray-700">
      {label}
      <select name={name} defaultValue={defaultValue} className="block w-full border rounded-lg p-2 mt-1 font-normal">
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    </label>
  );
}

function Checkbox({ name, label, defaultChecked }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
      <input type="checkbox" name={name} defaultChecked={defaultChecked} className="rounded" />
      {label}
    </label>
  );
}
